import React, { useState } from 'react';
import { Instagram, Youtube, Facebook, Twitter, Send, Leaf, ArrowUpRight } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Footer = ({ onBook }) => {
  const [email, setEmail] = useState('');

  const subscribe = (e) => {
    e.preventDefault();
    toast({ title: 'Welcome to the family!', description: 'You&apos;re subscribed to weekly nutrition insights.' });
    setEmail('');
  };

  return (
    <>
      {/* CTA banner */}
      <section className="py-20 bg-[#faf7f1]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-[3rem] bg-gradient-to-br from-[#3a5a2c] to-[#4d6f3d] overflow-hidden p-10 lg:p-16">
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -left-10 -bottom-10 w-60 h-60 rounded-full bg-[#c9743a]/30 blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-serif text-4xl lg:text-5xl text-white leading-tight">
                  Ready to feel your <em className="italic text-[#f4d5a8]">best</em>?
                </h2>
                <p className="text-white/80 mt-4 max-w-md">
                  Book your free 15-minute consultation call. We&apos;ll listen,
                  understand and craft a plan just for you.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <button
                  onClick={onBook}
                  className="h-14 px-8 rounded-full bg-white text-[#1a2b17] font-medium hover:bg-[#f4d5a8] transition inline-flex items-center justify-center gap-2 group"
                >
                  Book a free call
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                </button>
                <a
                  href="/pdfs/nutrifyme-corporate-profile.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-14 px-8 rounded-full border border-white/30 text-white hover:bg-white/10 transition inline-flex items-center justify-center"
                >
                  For companies · Corporate PDF
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#1a2b17] text-white pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_prachi-premium/artifacts/92rpbx3r_nutrify_me_logo.png"
                  alt="Nutrify Me logo"
                  className="w-11 h-11 object-contain bg-white rounded-full p-1"
                />
                <div>
                  <div className="font-serif text-xl">Nutrify Me</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/60">
                    by Nutritionist Prachi
                  </div>
                </div>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">
                A modern nutrition studio helping women & professionals get their
                energy, hormones and confidence back.
              </p>
              <div className="flex gap-3 mt-6">
                {[Instagram, Youtube, Facebook, Twitter].map((Icon, i) => (
                  <a
                    key={i}
                    href="#"
                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-[#c9743a] hover:border-[#c9743a] transition"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm tracking-widest uppercase text-white/70 mb-5">Company</h4>
              <ul className="space-y-3 text-sm text-white/80">
                <li><a href="/#about" className="hover:text-[#f4d5a8]">About Prachi</a></li>
                <li><a href="/#services" className="hover:text-[#f4d5a8]">Programs</a></li>
                <li><a href="/#products" className="hover:text-[#f4d5a8]">Shop</a></li>
                <li><a href="/#stories" className="hover:text-[#f4d5a8]">Success Stories</a></li>
                <li><a href="/blog" className="hover:text-[#f4d5a8]">Journal</a></li>
                <li><a href="/dietitian-in-kharadi-pune" className="hover:text-[#f4d5a8]">Kharadi Clinic</a></li>
                <li><a href="/dietitian-in-baner-pune" className="hover:text-[#f4d5a8]">Baner</a></li>
                <li><a href="/dietitian-in-hinjewadi-pune" className="hover:text-[#f4d5a8]">Hinjewadi</a></li>
                <li><a href="/dietitian-in-mumbai" className="hover:text-[#f4d5a8]">Mumbai</a></li>
                <li><a href="/pdfs/nutrifyme-corporate-profile.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-[#f4d5a8]">Corporate Diet Camps →</a></li>
                <li><a href="/pdfs/nutrifyme-pcos-guide.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-[#f4d5a8]">PCOS Guide (PDF) →</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm tracking-widest uppercase text-white/70 mb-5">Reach us</h4>
              <ul className="space-y-3 text-sm text-white/80">
                <li>A305, Panchshil Soho,<br />near Panchshil Towers, Kharadi,<br />Pune 411014</li>
                <li><a href="tel:+919545405936" className="hover:text-[#f4d5a8]">+91 95454 05936</a></li>
                <li><a href="mailto:hello@nutrifyme.com" className="hover:text-[#f4d5a8]">hello@nutrifyme.com</a></li>
                <li>Mon &ndash; Sat, 9am to 7pm</li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm tracking-widest uppercase text-white/70 mb-5">Newsletter</h4>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                Get the free “10-min Morning Routine for Fat Loss” and weekly tips.
              </p>
              <form onSubmit={subscribe} className="flex">
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="flex-1 h-11 px-4 rounded-l-full bg-white/10 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-white/40"
                />
                <button
                  type="submit"
                  className="h-11 w-12 rounded-r-full bg-[#c9743a] hover:bg-[#b26330] flex items-center justify-center transition"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/50">
            <div>&copy; {new Date().getFullYear()} NutrifyMe. All rights reserved.</div>
            <div className="flex gap-6">
              <a href="/privacy" className="hover:text-white">Privacy</a>
              <a href="/terms" className="hover:text-white">Terms</a>
              <a href="/refunds" className="hover:text-white">Refund policy</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
